/**
 * useFamilies - Hook for fetching families from Firestore (Admin)
 *
 * Provides cached, type-safe access to family data with automatic
 * loading and error states via React Query.
 *
 * Usage:
 * ```tsx
 * const { families, isLoading, error, refetch } = useFamilies();
 * ```
 */

import { orderBy, QueryConstraint } from 'firebase/firestore';
import { useFirestoreQuery, schoolCollection } from './useFirestoreQuery';
import { queryKeys } from './queryKeys';
import { useAuth } from './useAuth';
import type { Family } from '@shared/types';

export interface UseFamiliesOptions {
  /** Override schoolId (defaults to user's schoolId) */
  schoolId?: string;
  /** Whether to fetch (defaults to true when schoolId is available) */
  enabled?: boolean;
}

export interface UseFamiliesResult {
  /** List of families */
  families: Family[];
  /** Loading state */
  isLoading: boolean;
  /** Error if query failed */
  error: Error | null;
  /** Refetch function */
  refetch: () => void;
  /** Whether data is being refetched in background */
  isRefetching: boolean;
  /** Total families count */
  totalCount: number;
  /** Families with at least one registered parent */
  registeredCount: number;
  /** Families with no registered parents */
  pendingCount: number;
}

/**
 * Hook for fetching all families (admin only)
 */
export function useFamilies(options: UseFamiliesOptions = {}): UseFamiliesResult {
  const { customClaims } = useAuth();

  const schoolId = options.schoolId || customClaims?.schoolId;

  // Build query constraints
  const constraints: QueryConstraint[] = [
    orderBy('memberNo', 'desc'),
  ];

  const {
    data = [],
    isLoading,
    error,
    refetch,
    isRefetching,
  } = useFirestoreQuery<Family>({
    queryKey: queryKeys.school.families.all(schoolId || ''),
    collectionPath: schoolCollection(schoolId || '', 'families'),
    constraints,
    enabled: options.enabled ?? !!schoolId,
    idField: 'familyId',
  });

  // Compute counts
  const totalCount = data.length;
  const registeredCount = data.filter(
    (f) => f.parent1.userId || f.parent2?.userId
  ).length;
  const pendingCount = totalCount - registeredCount;

  return {
    families: data,
    isLoading,
    error,
    refetch,
    isRefetching,
    totalCount,
    registeredCount,
    pendingCount,
  };
}

/**
 * Hook for fetching a single family by ID
 */
export function useFamily(familyId: string | undefined) {
  const { customClaims } = useAuth();
  const schoolId = customClaims?.schoolId;

  const { data, isLoading, error, refetch } = useFirestoreQuery<Family>({
    queryKey: queryKeys.school.families.single(schoolId || '', familyId || ''),
    collectionPath: schoolCollection(schoolId || '', 'families'),
    constraints: [],
    enabled: !!schoolId && !!familyId,
    idField: 'familyId',
    // For single document, we'd ideally use useFirestoreDocument
    // but this works for fetching by familyId field
  });

  return {
    family: data?.[0] || null,
    isLoading,
    error,
    refetch,
  };
}
